import { FC, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2Icon, SendIcon } from 'lucide-react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { useAuthToken } from '@/hooks/useAuthToken';
import { createComment } from '@/api/comments';
import { Textarea } from '@/components/ui/textarea';
import { NbButton } from '@/components/Neobrut';

interface CommentFormProps {
  petId: number;
  parentId?: number;
  queryKey: string;
  onSuccess?: () => void;
  placeholder?: string;
  compact?: boolean;
}

export const CommentForm: FC<CommentFormProps> = ({
  petId,
  parentId,
  queryKey,
  onSuccess,
  placeholder = 'Escribí un comentario...',
  compact = false,
}) => {
  const navigate = useNavigate();
  const { token, isTokenExpired } = useAuthToken();
  const queryClient = useQueryClient();
  const [content, setContent] = useState('');

  const mutation = useMutation({
    mutationFn: () => createComment(content.trim(), petId, token!, parentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      setContent('');
      onSuccess?.();
      toast.success(parentId ? 'Respuesta enviada.' : 'Comentario publicado.');
    },
    onError: () => toast.error('No se pudo publicar el comentario.'),
  });

  const handleSubmit = () => {
    if (!token || isTokenExpired(token)) {
      toast.info('Iniciá sesión para comentar.');
      navigate('/login');
      return;
    }
    if (!content.trim()) return;
    mutation.mutate();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSubmit();
    }
  };

  const wrapperStyle = compact
    ? {}
    : {
        border: '2px solid var(--nb-ink)',
        background: 'var(--nb-paper)',
        boxShadow: '3px 3px 0 var(--nb-ink)',
        borderRadius: 'var(--nb-radius)',
      };

  return (
    <div className="flex flex-col gap-2 p-3" style={wrapperStyle}>
      <Textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="min-h-[70px] rounded-md border-2 border-[var(--nb-ink)] text-sm focus-visible:border-[var(--nb-ink)] focus-visible:ring-0"
        maxLength={500}
        disabled={mutation.isPending}
      />
      <div className="flex items-center justify-between gap-2">
        <span
          className="text-[10px] opacity-60"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          {content.length}/500 · Ctrl+Enter
        </span>
        <NbButton
          size="sm"
          fill="ink"
          onClick={handleSubmit}
          disabled={mutation.isPending || !content.trim()}
        >
          {mutation.isPending ? (
            <Loader2Icon size={12} className="animate-spin" />
          ) : (
            <SendIcon size={12} />
          )}
          {compact ? 'Responder' : 'Comentar'}
        </NbButton>
      </div>
    </div>
  );
};
